import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { setAuthToken } from '../api';

/**
 * Authentication Context
 *
 * Provides authentication state and functions throughout the app.
 * Manages user session, login, signup, logout, and profile data.
 *
 * Security features:
 * - Automatic token refresh
 * User profile from database
 */
export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  subscription_tier: 'free' | 'gold' | 'platinum';
  subscription_status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'inactive';
  subscription_current_period_end: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  api_calls_remaining: number | null;
  api_calls_reset_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Authentication context state
 */
interface AuthContextType {
  // Current user and session
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;

  // Loading states
  loading: boolean;
  profileLoading: boolean;
  profileError: Error | null;

  // Auth functions
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;

  // Profile functions
  refreshProfile: (options?: RefreshProfileOptions) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
}

interface FetchProfileOptions {
  force?: boolean;
  silent?: boolean;
}

type RefreshProfileOptions = FetchProfileOptions;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Component
 *
 * Wrap your app with this component to provide auth state to all children.
 *
 * Usage:
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Check if auth has been initialized before in this session
  const authInitialized = sessionStorage.getItem('auth_initialized') === 'true';

  // Try to restore cached auth data
  const getCachedUser = (): User | null => {
    try {
      const cached = sessionStorage.getItem('cached_user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const getCachedProfile = (): UserProfile | null => {
    try {
      const cached = sessionStorage.getItem('cached_profile');
      const cachedTime = sessionStorage.getItem('cached_profile_time');

      if (cached && cachedTime) {
        const age = Date.now() - parseInt(cachedTime);
        // Only use cached profile if it's less than 30 seconds old
        if (age < 30000) {
          return JSON.parse(cached);
        } else {
          sessionStorage.removeItem('cached_profile');
          sessionStorage.removeItem('cached_profile_time');
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState<User | null>(getCachedUser());
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(getCachedProfile());
  const profileRef = useRef<UserProfile | null>(profile);
  profileRef.current = profile;

  // Only show loading on first initialization, not on tab switches
  const [loading, setLoading] = useState(!authInitialized);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<Error | null>(null);

  // Track if Supabase is fully initialized (seen INITIAL_SESSION event)
  // Database queries will hang until this is true
  const [isSupabaseReady, setIsSupabaseReady] = useState(false);

  // Track in-flight profile fetches to prevent concurrent requests
  const profileFetchPromise = useRef<Promise<void> | null>(null);
  const lastProfileFetchTime = useRef<number>(0);

  // Flag to skip profile fetch during password updates
  const isUpdatingPassword = useRef<boolean>(false);

  /**
   * Fetch user profile from database
   */
  const fetchProfile = useCallback(async (userId: string, options: FetchProfileOptions = {}) => {
    const { force = false, silent = false } = options;

    // If there's already a fetch in progress, wait for it to complete
    if (profileFetchPromise.current) {
      try {
        await profileFetchPromise.current;
      } catch {
        // Fetch failed, will retry
      }

      // After waiting, the profile should be fresh - use it if available
      if (profileRef.current && !force) {
        return;
      }
    }

    // If not forced and profile was fetched recently (within 2 seconds), skip
    const timeSinceLastFetch = Date.now() - lastProfileFetchTime.current;
    if (!force && timeSinceLastFetch < 2000 && profileRef.current) {
      return;
    }

    if (force) {
      sessionStorage.removeItem('cached_profile');
      sessionStorage.removeItem('cached_profile_time');
      profileRef.current = null;
    }

    if (!force && profileRef.current) {
      return;
    }

    // Create the fetch promise
    const fetchPromise = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('[fetchProfile] Timeout reached after 10s, aborting query...');
        controller.abort();
      }, 10000); // 10s timeout

      try {
        if (!silent) {
          setProfileLoading(true);
        }
        setProfileError(null);

        console.log('[fetchProfile] Fetching profile for user:', userId, { force, silent });

        // Query the profile directly - the Supabase client already has the session
        // from onAuthStateChange, no need to call getSession() which can hang during token refresh
        const queryPromise = supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .abortSignal(controller.signal)
          .single();

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Query timeout after 10s')), 10000)
        );

        const result = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (result.error) {
          console.error('[fetchProfile] Supabase error:', result.error);
          throw new Error(`Failed to fetch profile: ${result.error.message}`);
        }

        console.log('[fetchProfile] Profile data received:', {
          id: result.data.id,
          email: result.data.email,
          subscription_tier: result.data.subscription_tier,
          subscription_status: result.data.subscription_status,
        });

        const normalizedProfile: UserProfile = {
          ...result.data,
        };

        setProfile(normalizedProfile);
        profileRef.current = normalizedProfile;
        sessionStorage.setItem('cached_profile', JSON.stringify(normalizedProfile));
        sessionStorage.setItem('cached_profile_time', Date.now().toString());
        lastProfileFetchTime.current = Date.now();
        console.log('[fetchProfile] Profile set successfully');
      } catch (error) {
        console.error('[fetchProfile] Error during fetch:', error);
        setProfileError(error as Error);
        // Profile fetch failed - rethrow to let caller handle it
        throw error;
      } finally {
        clearTimeout(timeoutId);
        if (!silent) {
          setProfileLoading(false);
        }
      }
    })();

    // Store the promise so other callers can wait for it
    profileFetchPromise.current = fetchPromise;

    try {
      await fetchPromise;
    } finally {
      // Clear the promise after completion
      profileFetchPromise.current = null;
    }
  }, []);




  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    let isInitialLoad = !authInitialized;

    // Only set timeout if this is truly the first load (increased to 8 seconds for slower connections)
    const loadingTimeout = isInitialLoad ? setTimeout(() => {
      setLoading(false);
      sessionStorage.setItem('auth_initialized', 'true');
      isInitialLoad = false;
    }, 8000) : null;

    // Track if Supabase has finished initialization
    // INITIAL_SESSION is the event that indicates Supabase is fully ready
    let hasSeenInitialSession = false;

    // Listen for auth changes (this is more reliable than getSession)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] onAuthStateChange event:', event);

      // Track when we've seen INITIAL_SESSION - this means Supabase is fully initialized
      if (event === 'INITIAL_SESSION') {
        hasSeenInitialSession = true;
        setIsSupabaseReady(true);
      }

      setSession(session);
      // Sync token with API client
      setAuthToken(session?.access_token ?? null);
      const newUser = session?.user ?? null;
      setUser(newUser);

      if (newUser && session) {
        console.log('[AuthContext] User logged in:', newUser.id, newUser.email);

        // Determine if we should fetch profile based on event type:
        // - INITIAL_SESSION: Supabase is fully initialized, safe to query
        // - TOKEN_REFRESHED: Token was just refreshed, safe to query
        // - SIGNED_IN: Only if we've already seen INITIAL_SESSION (fresh login)
        //   If SIGNED_IN fires before INITIAL_SESSION, it's a cached session being
        //   restored and Supabase isn't ready yet - queries will hang
        const shouldFetchProfile =
          event === 'INITIAL_SESSION' ||
          event === 'TOKEN_REFRESHED' ||
          (event === 'SIGNED_IN' && hasSeenInitialSession);

        if (!shouldFetchProfile) {
          console.log('[AuthContext] Skipping profile fetch - waiting for Supabase initialization (event:', event, ')');
          // Still cache user data and set loading to false
          sessionStorage.setItem('cached_user', JSON.stringify(newUser));
          if (isInitialLoad) {
            if (loadingTimeout) clearTimeout(loadingTimeout);
            setLoading(false);
            sessionStorage.setItem('auth_initialized', 'true');
            isInitialLoad = false;
          }
          return;
        }

        // Cache user data
        sessionStorage.setItem('cached_user', JSON.stringify(newUser));

        // Skip profile fetch if we're in the middle of a password update
        if (isUpdatingPassword.current) {
          console.log('[AuthContext] Skipping profile fetch during password update');
        } else {
          // Fetch profile - Supabase is ready
          try {
            console.log('[AuthContext] Fetching profile (event:', event, ')...');
            await fetchProfile(newUser.id, { force: true, silent: false });
            console.log('[AuthContext] Profile fetched successfully');
          } catch (error) {
            console.error('[AuthContext] Profile fetch failed:', error);
            setProfileError(error as Error);
          }
        }
      } else if (!newUser) {
        console.log('[AuthContext] User logged out, clearing profile');
        setProfile(null);
        // Clear cached data on logout
        sessionStorage.removeItem('cached_user');
        sessionStorage.removeItem('cached_profile');
      }

      // Set loading to false after auth state is processed (only on initial load)
      if (isInitialLoad) {
        if (loadingTimeout) clearTimeout(loadingTimeout);
        setLoading(false);
        sessionStorage.setItem('auth_initialized', 'true');
        isInitialLoad = false;
      }
    });

    // Note: We don't call getSession() here because it can hang during token refresh.
    // The onAuthStateChange listener above handles everything - it fires immediately
    // with the cached session from localStorage, and fires again after token refresh.
    console.log('[AuthContext] Auth listener set up, waiting for onAuthStateChange...');

    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign up a new user
   *
   * @param email - User's email address
   * @param password - User's password (min 8 characters)
   * @param fullName - User's full name (optional)
   * @returns Error object if signup failed
   */
  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        return { error };
      }

      // If Supabase didn't return an active session (can happen for some auth settings),
      // fall back to an explicit sign-in so the user lands in the app immediately.
      if (!data.session) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          return { error: signInError };
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: error as AuthError };
    }
  };

  /**
   * Sign in an existing user
   *
   * @param email - User's email address
   * @param password - User's password
   * @returns Error object if signin failed
   */
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as AuthError };
    }
  };

  /**
   * Sign out the current user
   *
   * Clears session, tokens, and local storage.
   */
  const signOut = async () => {
    // Clear local state immediately - don't wait for Supabase
    setUser(null);
    setSession(null);
    setAuthToken(null);
    setProfile(null);
    profileRef.current = null;

    // Clear all cached auth data
    sessionStorage.removeItem('auth_initialized');
    sessionStorage.removeItem('cached_user');
    sessionStorage.removeItem('cached_profile');

    // Clear Supabase auth token (custom storage key from supabase.ts)
    localStorage.removeItem('arber-auth-token');

    try {
      // Call Supabase signOut with a timeout to prevent hanging
      const signOutPromise = supabase.auth.signOut();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sign out timeout')), 2000)
      );

      await Promise.race([signOutPromise, timeoutPromise]);
    } catch {
      // Don't throw - we've already cleared local state
    }
  };

  /**
   * Send password reset email
   *
   * @param email - User's email address
   * @returns Error object if request failed
   */
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      return { error };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error: error as AuthError };
    }
  };

  /**
   * Update user's password
   *
   * User must be logged in.
   *
   * @param newPassword - New password (min 8 characters)
   * @returns Error object if update failed
   */
  const updatePassword = async (newPassword: string) => {
    try {
      console.log('[AuthContext] Updating password...');

      // Set flag to skip profile fetch during password update
      isUpdatingPassword.current = true;

      // Check if user is authenticated
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        console.error('[AuthContext] No active session');
        isUpdatingPassword.current = false;
        return { error: { message: 'You must be logged in to change your password' } as AuthError };
      }

      console.log('[AuthContext] Session exists, calling updateUser...');

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Supabase updateUser timed out')), 8000)
      );

      const updatePromise = supabase.auth.updateUser({
        password: newPassword,
      });

      const result = await Promise.race([updatePromise, timeoutPromise]) as any;

      console.log('[AuthContext] updateUser completed:', { hasError: !!result.error });

      if (result.error) {
        console.error('[AuthContext] Update password error:', result.error);
      }

      // Wait a bit for auth state change to complete, then reset flag
      setTimeout(() => {
        isUpdatingPassword.current = false;
        console.log('[AuthContext] Password update flag reset');
      }, 1000);

      return { error: result.error || null };
    } catch (error) {
      console.error('[AuthContext] Update password exception:', error);
      isUpdatingPassword.current = false;
      return { error: { message: error instanceof Error ? error.message : 'Failed to update password' } as AuthError };
    }
  };

  /**
   * Refresh user profile from database
   *
   * Useful after making updates to profile.
   * Will skip if Supabase isn't ready yet (queries would hang).
   */
  const refreshProfile = useCallback(async (options: RefreshProfileOptions = {}) => {
    if (!user || !session) {
      return;
    }

    // Check if Supabase is fully initialized - queries hang until INITIAL_SESSION
    if (!isSupabaseReady) {
      console.log('[refreshProfile] Skipping - Supabase not ready yet...');
      return;
    }

    await fetchProfile(user.id, {
      force: options.force ?? true,
      silent: options.silent ?? false,
    });
  }, [user, session, isSupabaseReady, fetchProfile]);

  /**
   * Update user profile
   *
   * @param updates - Partial profile object with fields to update
   * @returns Error object if update failed
   */
  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        return { error: new Error(error.message) };
      }

      // Refresh profile after update
      await refreshProfile();

      return { error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { error: error as Error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    profileError,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to use authentication context
 *
 * Usage:
 *   const { user, signIn, signOut } = useAuth();
 *
 * @throws Error if used outside AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook to check if user is authenticated
 *
 * Returns [isAuthenticated, loading]
 *
 * Usage:
 *   const [isAuthenticated, loading] = useIsAuthenticated();
 *   if (loading) return <Spinner />;
 *   if (!isAuthenticated) return <Login />;
 */
export function useIsAuthenticated(): [boolean, boolean] {
  const { user, loading } = useAuth();
  return [!!user, loading];
}

/**
 * Hook to require authentication
 *
 * Redirects to login if not authenticated.
 * Returns user or null while loading.
 *
 * Usage:
 *   const user = useRequireAuth();
 *   if (!user) return <LoadingSpinner />;
 *   return <div>Hello {user.email}</div>;
 */
export function useRequireAuth() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      // Will be handled by ProtectedRoute component
    }
  }, [user, loading]);

  return user;
}
